// Interactive controls shared by the chart cards: the segmented range control, the "Reset zoom" button and
// the toggle chips that double as legend and readout.
import * as React from 'react';

import { LayoutSize } from '../../util';
import { CARD_BG, FAINT, HAIRLINE, HAIRLINE_STRONG, INK, PAGE_BG, SECONDARY } from './tokens';

export function Segmented<K extends string>(props: {
  label: string;
  options: { key: K; label: string }[];
  value: K | null; // null: none selected (e.g. while zoomed)
  onPick: (key: K) => void;
  size: LayoutSize;
  stretch?: boolean; // fill the row, buttons share the width
}): React.ReactElement {
  const { size } = props;
  const phone = size === 'phone';
  return (
    <div
      role="group"
      aria-label={props.label}
      style={{ display: 'flex', gap: 2, padding: 3, background: HAIRLINE, borderRadius: 10 }}
    >
      {props.options.map((o) => {
        const on = props.value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={on}
            onClick={() => props.onPick(o.key)}
            style={{
              flex: props.stretch ? 1 : undefined,
              border: 0,
              minHeight: size === 'web' ? 34 : 40,
              padding: props.stretch ? 0 : phone ? '0 11px' : '0 14px',
              fontSize: phone ? 12 : 13,
              fontWeight: 600,
              borderRadius: 8,
              background: on ? CARD_BG : 'transparent',
              color: on ? INK : SECONDARY,
              boxShadow: on ? '0 1px 2px rgba(11,11,11,0.08)' : undefined,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function ResetZoomButton(props: { size: LayoutSize; onClick: () => void }): React.ReactElement {
  const phone = props.size === 'phone';
  return (
    <button
      type="button"
      onClick={props.onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: props.size === 'web' ? 34 : 44,
        padding: phone ? '0 10px' : '0 12px',
        fontSize: phone ? 12 : 13,
        fontWeight: 600,
        color: '#1c5cab',
        background: '#e3eefb',
        border: 0,
        borderRadius: 8,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1c5cab"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M8 11h6M20 20l-4-4" />
      </svg>
      Reset zoom
    </button>
  );
}

// A series chip: colour mark, name and the value at the cursor; tapping toggles the series.
export function ToggleChip(props: {
  name: string;
  color: string;
  on: boolean;
  value: React.ReactNode;
  onToggle: () => void;
  size: LayoutSize;
}): React.ReactElement {
  const { on } = props;
  const phone = props.size === 'phone';
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={props.onToggle}
      className="num"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        minHeight: phone ? 34 : 36,
        padding: phone ? '0 10px' : '0 12px',
        fontSize: phone ? 12 : 13,
        borderRadius: 999,
        cursor: 'pointer',
        background: on ? CARD_BG : PAGE_BG,
        border: `1px solid ${on ? HAIRLINE_STRONG : HAIRLINE}`,
        color: on ? INK : FAINT,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{ width: phone ? 10 : 12, height: 3, borderRadius: 2, background: on ? props.color : '#c3c2b7' }}
      />
      {props.name} <b>{on ? props.value : 'off'}</b>
    </button>
  );
}
